import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import jwtDecode from "jwt-decode";
import { createBrowserHistory } from "history";

import App from "./app/App";
import Actions from "./app/login/duck/actions";
import store, { persistor } from "./store";
import reportWebVitals from "./reportWebVitals";
import WebSocketProvider from "./webSocket/webSocket";
import NotificationsWebSocketContext from "./webSocket/notificationSocket";
import "./index.css";
import "react-toastify/dist/ReactToastify.css";
import Operations from "./app/login/duck/operations";
import { activityBackendSync } from "./app/common/activity/activityBackendSync";
import activity from "./app/common/activity/activityLogger";
import { PersistGate } from "redux-persist/integration/react";
import { GOOGLE_OAUTH_CLIENT_ID, SENTRY_DSN } from "./app/common/constants";
import "react-tippy/dist/tippy.css";

// sentry for error tracking
import * as Sentry from "@sentry/react";
import SentryErrorPage from "./app/common/components/SentryErrorPage";
import UserPresence from "./app/presence/PresenceContainer";

/**  Redirect to dashboard if user already logged in */
let user = {};
try {
  user = JSON.parse(localStorage.getItem("user"));
} catch (e) {
  store.dispatch(Actions.logout());
  window.location.reload();
}

const authenticate = () => {
  let accessToken = null;
  let refreshToken = null;
  let user = null;
  try {
    accessToken = localStorage.getItem("accessToken");
    refreshToken = localStorage.getItem("refreshToken");
    user = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    console.error("Local storage was corrupted.");
  }
  if (accessToken !== null && accessToken !== "" && user !== null) {
    try {
      if (jwtDecode(accessToken)) {
        store.dispatch(
          Operations.loginUserSuccess(accessToken, refreshToken, user)
        );
      }
    } catch (err) {
      console.error("Invalid token");
      store.dispatch(Actions.logout());
      window.location.reload();
    }
  } else {
    window.onload = function () {
      // Disable this inside i frames
      if (
        window.self === window.top &&
        localStorage.getItem("platformInvite") === null &&
        window.location.hostname !== "localhost" &&
        !["sop-builder"].some(function (part) {
          return window.location.pathname.includes(part);
        })
      ) {
        window.google?.accounts.id.initialize({
          client_id: `${GOOGLE_OAUTH_CLIENT_ID}`,
          callback: ({ credential }) => {
            store.dispatch(
              Operations.loginUser({
                providerName: "google",
                idToken: credential,
                inviteCode: new URL(window.location.href).searchParams.get(
                  "inviteCode"
                ),
                role: new URL(window.location.href).searchParams.get("role"),
              })
            );
          },
          auto_select: false,
          prompt_parent_id: "g_id_onload",
          cancel_on_tap_outside: true,
        });
        window.google?.accounts.id.prompt(); // also display the One Tap dialog
      }
    };
  }
};
authenticate();
window.onstorage = (e) => {
  if (e.key === "accessToken" && (e.oldValue === null || e.newValue === null)) {
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }
};

const history = createBrowserHistory();

const SentryProvider = ({ children }) => {
  useEffect(() => {
    if (import.meta.env.PROD) {
      Sentry.init({
        dsn: SENTRY_DSN,
        replaysSessionSampleRate: 0.05,
        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.reactRouterV5BrowserTracingIntegration({
            history,
            tracePropagationTargets: [
              "nbyula.com",
              "nbl.one",
              "api.nbl.one",
              "api.nbyula.com",
              "argonaut.nbyula.com",
              "localhost",
              "127.0.0.1",
            ],
          }),
          Sentry.replayIntegration({
            maskAllText: false,
          }),
        ],
        tracesSampleRate: 1,
      });
    }
  }, []);

  if (!import.meta.env.PROD) {
    return children;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => <SentryErrorPage error={error} user={user} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

createRoot(document.getElementById("root")).render(
  <SentryProvider>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <UserPresence>
          <NotificationsWebSocketContext>
            <WebSocketProvider>
              <App history={history} />
            </WebSocketProvider>
          </NotificationsWebSocketContext>
        </UserPresence>
      </PersistGate>
    </Provider>
  </SentryProvider>
);

reportWebVitals(console.log);
activityBackendSync();
activity.initializeActivityLogger();
